import { Head, Link } from "@inertiajs/react";
import { ArrowLeft, Plus } from "lucide-react";
import { useState } from "react";
import BizminerAdminLayout from "@/bizminer/layouts/admin/BizminerAdminLayout.tsx";
import { Button } from "@/components/ui/button";
import { bizminer_admin_organization_path } from "@/routes";
import type { PagyMetadata } from "@/types/Pagy";
import type {
  BizminerAdminCreditsTransaction,
  BizminerAdminOrganization,
} from "@/types/serializers";
import CreditTransactionsTable from "../_components/CreditTransactionsTable";
import GrantCreditsModal from "../_components/GrantCreditsModal";

interface SubscriptionPlan {
  id: number;
  name: string;
  credits_per_month: number;
  description: string | null;
}

interface Props {
  organization: BizminerAdminOrganization;
  transactions: BizminerAdminCreditsTransaction[];
  available_plans: SubscriptionPlan[];
  pagy: PagyMetadata;
}

// File-local components

interface StatCardProps {
  label: string;
  value: string;
  subtitle: string;
}

const StatCard = ({ label, value, subtitle }: StatCardProps) => (
  <div className="bg-muted/50 rounded-lg p-4">
    <p className="text-sm font-medium text-muted-foreground">{label}</p>
    <p className="text-3xl font-bold text-foreground mt-1">{value}</p>
    <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>
  </div>
);

interface BalanceCardProps {
  balance: number;
}

const BalanceCard = ({ balance }: BalanceCardProps) => (
  <StatCard
    label="Current Balance"
    value={balance.toLocaleString()}
    subtitle="credits"
  />
);

interface UsageCardProps {
  spent: number;
}

const UsageCard = ({ spent }: UsageCardProps) => (
  <StatCard
    label="Used This Month"
    value={spent.toLocaleString()}
    subtitle="credits"
  />
);

interface PageHeaderProps {
  organizationId: number;
  organizationName: string;
  onGrantClick: () => void;
}

const PageHeader = ({
  organizationId,
  organizationName,
  onGrantClick,
}: PageHeaderProps) => (
  <div className="flex items-center justify-between">
    <div className="flex items-center gap-4">
      <Link
        href={bizminer_admin_organization_path(organizationId)}
        className="text-muted-foreground hover:text-foreground transition-colors"
      >
        <ArrowLeft className="h-5 w-5" />
      </Link>
      <div>
        <h1 className="text-2xl font-bold text-foreground">
          Credit Management
        </h1>
        <p className="text-sm text-muted-foreground mt-1">{organizationName}</p>
      </div>
    </div>

    <div className="flex items-center gap-3">
      <Button onClick={onGrantClick}>
        <Plus className="h-4 w-4 mr-2" />
        Grant Credits
      </Button>
    </div>
  </div>
);

interface StatsGridProps {
  balance: number;
  spent: number;
}

const StatsGrid = ({ balance, spent }: StatsGridProps) => (
  <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
    <BalanceCard balance={balance} />
    <UsageCard spent={spent} />
  </div>
);

export default function Index({ organization, transactions, pagy }: Props) {
  const [isGrantModalOpen, setIsGrantModalOpen] = useState(false);

  return (
    <BizminerAdminLayout>
      <Head title={`Credits - ${organization.name} - Super Admin`} />

      <div className="min-h-screen">
        <div className="bg-card border-b">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <PageHeader
              organizationId={organization.id}
              organizationName={organization.name}
              onGrantClick={() => setIsGrantModalOpen(true)}
            />

            <StatsGrid
              balance={organization.credits_balance}
              spent={organization.credits_spent_this_month}
            />
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
          <CreditTransactionsTable
            transactions={transactions}
            organizationId={organization.id}
            pagy={pagy}
          />
        </div>
      </div>

      <GrantCreditsModal
        open={isGrantModalOpen}
        onClose={() => setIsGrantModalOpen(false)}
        organizationId={organization.id}
        organizationName={organization.name}
        currentBalance={organization.credits_balance}
      />
    </BizminerAdminLayout>
  );
}
